import * as Sentry from '@sentry/react-native';
import Constants from 'expo-constants';
import { registerCrashReporter } from './crashReporter';
import { AppError } from './errorHandler';
import { createPrefixedLogger } from './logger';

const log = createPrefixedLogger('sentry');

let initialized = false;

export function initSentry() {
  if (initialized) return;

  const dsn = process.env.EXPO_PUBLIC_SENTRY_DSN;
  if (!dsn) {
    log.warn('EXPO_PUBLIC_SENTRY_DSN is not set — Sentry disabled.');
    return;
  }

  const appEnv =
    (Constants.expoConfig?.extra as { APP_ENV?: string } | undefined)?.APP_ENV ?? 'dev';
  const release = Constants.expoConfig?.version
    ? `redi@${Constants.expoConfig.version}`
    : undefined;

  Sentry.init({
    dsn,
    environment: appEnv,
    release,
    // `dist` pairs with `release` to key source-map uploads. Keep this
    // in sync with the `--dist` arg in the web:upload-sourcemaps npm
    // script; mismatches cause Sentry to show minified traces.
    dist: appEnv,
    enableAutoPerformanceTracing: true,
    tracesSampleRate: appEnv === 'production' ? 0.2 : 1.0,
    // Drop personally identifying fields before sending.
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }
      return event;
    },
  });

  // Route uncaught React render errors captured by ErrorBoundary into Sentry.
  registerCrashReporter((error, componentStack) => {
    Sentry.captureException(error, {
      contexts: componentStack ? { react: { componentStack } } : undefined,
    });
  });

  initialized = true;
}

// Convenience for call-sites that already have an AppError. Tags the event
// with the AppError code so Sentry can group/filter by error class.
export function captureAppError(error: unknown, context?: Record<string, unknown>) {
  if (!initialized) return;
  const tags = error instanceof AppError ? { appErrorCode: error.code } : undefined;
  Sentry.captureException(error, { tags, extra: context });
}
